import * as Haptics from "expo-haptics";

import { Box, useTheme, AHIcon } from "arkh";
import {
  InteractionManager,
  Platform,
  TouchableOpacity,
  View,
  ViewStyle,
} from "react-native";
import React, { useCallback, useMemo } from "react";
import { getColor, getIconName } from "../../../../common/OptionTaskUtils";

import { TaskExecutionProps } from "./TaskExecutionProps";

import { graphql, useFragment, useMutation } from "react-relay";
import { TaskExecutionOptionsComponent$key } from "./__generated__/TaskExecutionOptionsComponent.graphql";
import { TaskExecutionOptionsSaveValueMutation } from "./__generated__/TaskExecutionOptionsSaveValueMutation.graphql";
import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";

const Option = ({
  sentiment,
  selected,
  onPress,
  disabled,
  multiple,
}: {
  sentiment: "POSITIVE" | "NEUTRAL" | "NEGATIVE";
  selected?: boolean;
  onPress: (sentiment: "POSITIVE" | "NEUTRAL" | "NEGATIVE") => void;
  disabled?: boolean;
  multiple?: boolean;
}) => {
  const theme = useTheme();
  const circleStyle: ViewStyle = useMemo(
    () => ({
      borderRadius: multiple ? 2 : 20,
      width: 40,
      height: 40,
      borderColor: getColor(sentiment, theme),
      backgroundColor: selected ? getColor(sentiment, theme) : "transparent",
      borderWidth: 2,
      alignItems: "center",
      justifyContent: "center",
    }),
    [multiple, selected, sentiment, theme]
  );
  const iconName = useMemo(() => getIconName(sentiment), [sentiment]);
  const touchableStyle: ViewStyle = useMemo(
    () => ({
      flex: 1,
      opacity: disabled ? 0.5 : 1,
      flexDirection: "row",
      marginTop: 15,
      marginBottom: 5,
      maxWidth: 200,
    }),
    [disabled]
  );
  return (
    <TouchableOpacity
      testID={`TaskExecutionOption_${sentiment}`}
      hitSlop={{ top: 10, bottom: 10 }}
      disabled={disabled}
      onPressIn={() => {
        if (Platform.OS === "ios") {
          Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
        }
      }}
      onPress={() => {
        onPress(sentiment);
      }}
      style={touchableStyle}
    >
      <View style={circleStyle}>
        <AHIcon type={iconName} customColor="white" forceSize={23} />
      </View>
    </TouchableOpacity>
  );
};

type Props = Omit<
  TaskExecutionProps & {
    taskExecution: TaskExecutionOptionsComponent$key;
  },
  "openIssue" | "openIssueFull" | "openInstructions" | "openImages"
>;

const TaskExecutionOptionsCmp = (props: Props) => {
  const taskExecution = useFragment(
    graphql`
      fragment TaskExecutionOptionsComponent on TaskExecutionCheck {
        id
        task {
          sentiments
        }
        checkValue
      }
    `,
    props.taskExecution
  );

  const { currentEmployeeId } = usePersistedEmployeeId();

  const [save] = useMutation<TaskExecutionOptionsSaveValueMutation>(graphql`
    mutation TaskExecutionOptionsSaveValueMutation(
      $id: ID!
      $value: TaskSentiment
      $employeeId: ID
    ) @raw_response_type {
      setTaskExecutionCheck(
        id: $id
        sentiment: $value
        employeeId: $employeeId
      ) {
        id
        checkValue
        isCompleted
        ...TaskExecutionListItemUpdatedInfo_taskExecution
      }
    }
  `);

  const onSave = useCallback(
    (clickedVal: "POSITIVE" | "NEGATIVE" | "NEUTRAL") => {
      const val = clickedVal === taskExecution.checkValue ? null : clickedVal;

      save({
        variables: {
          id: taskExecution.id,
          value: val,
          employeeId: currentEmployeeId,
        },
        optimisticResponse: {
          setTaskExecutionCheck: {
            checkValue: val,
            id: taskExecution.id,
            isCompleted: true,
            __isTaskExecution: "TaskExecutionCheck",
            lastUpdatedByActor: null,
            updatedAtDate: null,
          },
        },
      });
      if (val !== "NEGATIVE") {
        InteractionManager.runAfterInteractions(() => {
          props.onNext?.();
        });
      } else {
        InteractionManager.runAfterInteractions(() => {
          props.onFocus?.();
        });
      }
    },
    [currentEmployeeId, props, save, taskExecution.checkValue, taskExecution.id]
  );

  return (
    <>
      <View
        style={{
          flexDirection: "column",
          flex: 1,
          paddingVertical: 0,
        }}
      >
        <Box spaceRight="large" style={{ flexDirection: "row" }}>
          {taskExecution.task.sentiments.includes("POSITIVE") ||
          taskExecution.checkValue === "POSITIVE" ? (
            <Option
              selected={taskExecution.checkValue === "POSITIVE"}
              sentiment="POSITIVE"
              onPress={onSave}
              disabled={props.disabled}
            />
          ) : null}
          {taskExecution.task.sentiments.includes("NEUTRAL") ||
          taskExecution.checkValue === "NEUTRAL" ? (
            <Option
              selected={taskExecution.checkValue === "NEUTRAL"}
              sentiment="NEUTRAL"
              onPress={onSave}
              disabled={props.disabled}
            />
          ) : null}
          {taskExecution.task.sentiments.includes("NEGATIVE") ||
          taskExecution.checkValue === "NEGATIVE" ? (
            <Option
              selected={taskExecution.checkValue === "NEGATIVE"}
              sentiment="NEGATIVE"
              onPress={onSave}
              disabled={props.disabled}
            />
          ) : null}
        </Box>
      </View>
    </>
  );
};

export const TaskExecutionOptions = React.memo(TaskExecutionOptionsCmp);
